import {SIZE_RECRUITMENT_TEXTURE_KEYS} from '../../configs/sizeRecruitmentMarker';

/**
 * Текстуры маркера докупки размера (см. configs/sizeRecruitmentMarker): пиксели
 * считаются кодом и регистрируются через textures.addUint8Array, как и у анимаций
 * способностей (utils/effects/abilityCastTextures).
 *
 * Медальон — в своих цветах; ореол и человечки БЕЛЫЕ, красятся tint'ом: так
 * человечки становятся и золотыми на медальоне, и темными на летящем шаре.
 */

export const SIZE_RECRUITMENT_TEXTURE_SIZES = {
  [SIZE_RECRUITMENT_TEXTURE_KEYS.PLATE]: {width: 256, height: 256},
  [SIZE_RECRUITMENT_TEXTURE_KEYS.PLATE_SILVER]: {width: 256, height: 256},
  [SIZE_RECRUITMENT_TEXTURE_KEYS.HALO]: {width: 128, height: 128},
  [SIZE_RECRUITMENT_TEXTURE_KEYS.PEOPLE]: {width: 128, height: 128},
};

const SUPERSAMPLE = 3;

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const mix = (from, to, share) => from + (to - from) * clamp01(share);
const mixRgb = (from, to, share) => from.map((channel, index) => mix(channel, to[index], share));

// Расстояние до шестиугольника с плоским верхом (<0 — внутри), apothem — от
// центра до стороны, corner — скругление углов
export const hexDistance = (x, y, apothem, corner = 0) => {
  const inner = apothem - corner;
  let px = Math.abs(x);
  let py = Math.abs(y);
  const fold = 2 * Math.min(-0.866025404 * px + 0.5 * py, 0);
  px -= fold * -0.866025404;
  py -= fold * 0.5;
  const limit = 0.577350269 * inner;
  const dx = px - Math.min(limit, Math.max(-limit, px));
  const dy = py - inner;
  return Math.hypot(dx, dy) * Math.sign(dy) - corner;
};

// Цвет и прозрачность точки → пиксель: подвыборки сглаживают край, а цвет
// считается по видимым подвыборкам, поэтому край не темнеет
export const rasterizePixels = (width, height, sample) => {
  const pixels = new Uint8Array(width * height * 4);
  const subSamples = SUPERSAMPLE * SUPERSAMPLE;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let [red, green, blue, alpha] = [0, 0, 0, 0];
      for (let subY = 0; subY < SUPERSAMPLE; subY += 1) {
        for (let subX = 0; subX < SUPERSAMPLE; subX += 1) {
          const [r, g, b, a] = sample(
            x + (subX + 0.5) / SUPERSAMPLE - width / 2,
            y + (subY + 0.5) / SUPERSAMPLE - height / 2,
          );
          red += r * a;
          green += g * a;
          blue += b * a;
          alpha += a;
        }
      }
      if (alpha > 0) {
        const offset = (y * width + x) * 4;
        pixels[offset] = Math.round(red / alpha);
        pixels[offset + 1] = Math.round(green / alpha);
        pixels[offset + 2] = Math.round(blue / alpha);
        pixels[offset + 3] = Math.round(255 * clamp01(alpha / subSamples));
      }
    }
  }
  return pixels;
};

const rasterize = (key, sample) => {
  const {width, height} = SIZE_RECRUITMENT_TEXTURE_SIZES[key];
  return rasterizePixels(width, height, sample);
};

const GOLD_PLATE_COLORS = {
  rimLight: [255, 240, 170],
  rim: [255, 190, 40],
  rimDark: [176, 104, 12],
  groove: [38, 20, 6],
  line: [214, 150, 36],
  insideTop: [66, 44, 26],
  insideBottom: [26, 16, 9],
  insideGlow: [96, 60, 18],
  shadow: [18, 9, 2],
};

const SILVER_PLATE_COLORS = {
  rimLight: [255, 255, 255],
  rim: [206, 218, 235],
  rimDark: [112, 126, 148],
  groove: [16, 20, 28],
  line: [168, 184, 208],
  insideTop: [52, 58, 72],
  insideBottom: [20, 24, 33],
  insideGlow: [78, 90, 112],
  shadow: [8, 10, 16],
};

const PLATE_APOTHEM = 102;
const PLATE_CORNER = 9;
const PLATE_RIM_WIDTH = 10;
const PLATE_GROOVE_WIDTH = 3;
const PLATE_LINE_WIDTH = 2.5;
const PLATE_SHADOW_WIDTH = 13;

/**
 * Медальон: темная тень вокруг, золотая оправа с бликом сверху и фаской, темная
 * канавка, тонкая золотая линия и темное поле с теплым светом от центра
 */
const samplePlate = (colors) => (x, y) => {
  const distance = hexDistance(x, y, PLATE_APOTHEM, PLATE_CORNER);
  const height = (y + PLATE_APOTHEM) / (2 * PLATE_APOTHEM);
  if (distance > 0) {
    const fade = 1 - distance / PLATE_SHADOW_WIDTH;
    return fade > 0 ? [...colors.shadow, 0.7 * fade * fade] : [0, 0, 0, 0];
  }
  const depth = -distance;
  if (depth < PLATE_RIM_WIDTH) {
    // Фаска: светлее посередине оправы, сверху оправа светлее, снизу темнее
    const bevel = Math.sin(depth / PLATE_RIM_WIDTH * Math.PI);
    const base = height < 0.5
      ? mixRgb(colors.rimLight, colors.rim, height * 2)
      : mixRgb(colors.rim, colors.rimDark, height * 2 - 1);
    return [...mixRgb(mixRgb(base, colors.rimDark, 0.45), base, bevel), 1];
  }
  if (depth < PLATE_RIM_WIDTH + PLATE_GROOVE_WIDTH) {
    return [...colors.groove, 1];
  }
  if (depth < PLATE_RIM_WIDTH + PLATE_GROOVE_WIDTH + PLATE_LINE_WIDTH) {
    return [...colors.line, 1];
  }
  const glow = 1 - Math.hypot(x, y) / PLATE_APOTHEM;
  return [...mixRgb(mixRgb(colors.insideTop, colors.insideBottom, height), colors.insideGlow, glow * glow * 0.8), 1];
};

export const buildSizeRecruitmentPlatePixels = () => rasterize(
  SIZE_RECRUITMENT_TEXTURE_KEYS.PLATE, samplePlate(GOLD_PLATE_COLORS),
);

export const buildSizeRecruitmentSilverPlatePixels = () => rasterize(
  SIZE_RECRUITMENT_TEXTURE_KEYS.PLATE_SILVER, samplePlate(SILVER_PLATE_COLORS),
);

/**
 * Такой же медальон в других цветах и другого размера (маленькие живые маркеры,
 * см. liveMarkerTextures): на картинке помельче его края чище при уменьшении
 * @param {number} size — сторона картинки
 * @param {Object} colors — как GOLD_PLATE_COLORS
 */
export const buildMedallionPixels = (size, colors) => {
  const sample = samplePlate(colors);
  const fullSize = 256 / size;
  return rasterizePixels(size, size, (x, y) => sample(x * fullSize, y * fullSize));
};

const HALO_APOTHEM = 30;

/** Ореол: плотный у шестиугольника и мягко гаснущий к краю картинки */
export const buildSizeRecruitmentHaloPixels = () => rasterize(SIZE_RECRUITMENT_TEXTURE_KEYS.HALO, (x, y) => {
  const fade = 1 - Math.max(0, hexDistance(x, y, HALO_APOTHEM, 12)) / 26;
  return [255, 255, 255, fade > 0 ? fade * fade * 0.95 : 0];
});

// Человечки — в координатах исходной иконки /img/icons/unit size.png (512 × 512)
const PEOPLE_SCALE = 512 / 128;
const PEOPLE_HEADS = [[256, 128, 88], [100, 188, 66], [412, 188, 66]];
const PEOPLE_BODIES = [[137, 235, 375, 472, 48], [4, 272, 110, 472, 46], [402, 272, 508, 472, 46]];

const checkIfInRoundedRect = (x, y, [left, top, right, bottom, radius]) => {
  const dx = Math.max(left + radius - x, 0, x - right + radius);
  const dy = Math.max(top + radius - y, 0, y - bottom + radius);
  return Math.hypot(dx, dy) <= radius;
};

/** Белые человечки: та же фигура, что на иконке размера отряда */
export const buildSizeRecruitmentPeoplePixels = () => rasterize(SIZE_RECRUITMENT_TEXTURE_KEYS.PEOPLE, (x, y) => {
  const iconX = (x + 64) * PEOPLE_SCALE;
  const iconY = (y + 64) * PEOPLE_SCALE;
  const isInside = PEOPLE_HEADS.some(([cx, cy, radius]) => Math.hypot(iconX - cx, iconY - cy) <= radius)
    || PEOPLE_BODIES.some((body) => checkIfInRoundedRect(iconX, iconY, body));
  return [255, 255, 255, isInside ? 1 : 0];
});

const PIXEL_BUILDERS = {
  [SIZE_RECRUITMENT_TEXTURE_KEYS.PLATE]: buildSizeRecruitmentPlatePixels,
  [SIZE_RECRUITMENT_TEXTURE_KEYS.PLATE_SILVER]: buildSizeRecruitmentSilverPlatePixels,
  [SIZE_RECRUITMENT_TEXTURE_KEYS.HALO]: buildSizeRecruitmentHaloPixels,
  [SIZE_RECRUITMENT_TEXTURE_KEYS.PEOPLE]: buildSizeRecruitmentPeoplePixels,
};

/**
 * Зарегистрировать текстуры один раз на сцену
 * @param {Phaser.Scene} scene
 * @param {Object} [options]
 * @param {number} [options.linearFilter] — режим фильтрации LINEAR
 * @returns {boolean} готовы ли текстуры
 */
export const ensureSizeRecruitmentTextures = (scene, {linearFilter} = {}) => {
  const textures = scene?.textures;
  if (!textures?.exists || !textures.addUint8Array) {
    return false;
  }

  return Object.values(SIZE_RECRUITMENT_TEXTURE_KEYS).every((key) => {
    if (textures.exists(key)) {
      return true;
    }
    const {width, height} = SIZE_RECRUITMENT_TEXTURE_SIZES[key];
    const texture = textures.addUint8Array(key, PIXEL_BUILDERS[key](), width, height);
    if (!texture) {
      return false;
    }
    if (linearFilter !== undefined) {
      texture.setFilter?.(linearFilter);
    }
    return true;
  });
};
